import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import GrowthShell, { Icon } from '../../components/growth/GrowthShell';
import { CLARITY_URL, fetchDashboard } from '../../lib/growthDashboard';
import { fetchClarity } from '../../lib/growthClarity';
import { fetchAds, summarizeAds } from '../../lib/growthAds';
import RangeBar from '../../components/growth/GrowthRange';
import ClaritySessions from '../../components/growth/ClaritySessions';
import { MIN_SAMPLE, rangeLabel, rangeWindow, readRange, spanLabel, writeRange } from '../../lib/growthRange';

// Growth OS — لوحة التحكم. Layout follows the spec's mockup; the numbers do
// not: every figure is counted from real rows (growth_trader_events, paid
// market_orders), a measure with nothing behind it says «لا توجد بيانات بعد»,
// and a source we are not connected to says «غير متصل». Owner, 2026-11-01.
//
// One range drives every card, chart and the funnel; it lives in the URL.
// A percentage resting on fewer than MIN_SAMPLE is greyed with a note — a big
// red «100%» from two sessions is a misreading, not a finding.

const TRACKING_SINCE = '1 نوفمبر 2026';
const ORDERS_VS_TRACKING = `الطلبات من سجل الطلبات الفعلي، ورحلة العميل من التتبّع الذي بدأ في ${TRACKING_SINCE}.`;
const LOW_NOTE = 'بيانات قليلة، لا تعتمد على النسبة بعد';
const fmt = (n) => Number(n || 0).toLocaleString('en-US');
const sar = (n) => `${Number(n || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })} ر.س`;

function Change({ now, before, compare, hasPrev, sampleNow, samplePrev }) {
  if (!compare || !hasPrev || !before) return null;
  const pct = Math.round(((now - before) / before) * 100);
  const low = Math.min(sampleNow, samplePrev) < MIN_SAMPLE;
  if (low) {
    return (
      <span className="gos-low" title={`${LOW_NOTE} (الحد ${MIN_SAMPLE} في كل فترة)`} data-low-sample="1">
        <span className="gos-pill low">{pct > 0 ? '▲' : pct < 0 ? '▼' : ''} {Math.abs(pct)}%</span> بيانات قليلة
      </span>
    );
  }
  if (pct === 0) return <span className="gos-pill neutral">0%</span>;
  return <span className={`gos-pill ${pct > 0 ? 'good' : 'bad'}`}>{pct > 0 ? '▲' : '▼'} {Math.abs(pct)}%</span>;
}

function Kpi({ label, value, change, empty, off, rtlValue, info }) {
  return (
    <div className="gos-kpi" data-kpi={label} title={info || undefined}>
      <p className="gos-kpi-label">
        {label}
        {info && <span className="gos-info" role="img" aria-label={info} title={info}>ⓘ</span>}
      </p>
      {off ? <p className="gos-kpi-value muted">غير متصل</p> : <p className="gos-kpi-value" style={rtlValue ? { direction: 'rtl' } : undefined}>{value}</p>}
      <div className="gos-kpi-foot">
        {off ? 'لا يوجد ربط بالمنصات الإعلانية' : empty ? 'لا توجد بيانات بعد' : change}
      </div>
    </div>
  );
}

// One measure per chart (small multiples on a shared time axis) — never two y-axes.
function Bars({ title, rows, field, total, hourly }) {
  const W = 300; const H = 64; const n = rows.length;
  const max = Math.max(1, ...rows.map((r) => r[field]));
  const slot = W / n; const bw = Math.max(1, slot - 2);
  const edge = (r) => (hourly ? r.label : r.label.slice(5));
  return (
    <div className="gos-chart" data-chart={field}>
      <div className="gos-mult-title"><span>{title}</span><b>{fmt(total)}</b></div>
      <svg viewBox={`0 0 ${W} ${H + 14}`} direction="ltr" role="img" aria-label={`${title}: ${fmt(total)}`}>
        <line x1="0" x2={W} y1={H} y2={H} stroke="var(--gos-grid)" strokeWidth="1" />
        {rows.map((r, i) => {
          const h = r[field] ? Math.max(2, (r[field] / max) * (H - 4)) : 0;
          // RTL page: earliest on the right, latest on the left
          const x = W - (i + 1) * slot + 1;
          return (
            <g key={r.key}>
              <rect x={x} y={0} width={bw} height={H} fill="transparent"><title>{`${r.label}: ${fmt(r[field])}`}</title></rect>
              {h > 0 && <rect className="bar" x={x} y={H - h} width={bw} height={h} rx="1.5" fill="var(--gos-gold)" pointerEvents="none" />}
            </g>
          );
        })}
        <text className="gos-axis" x={W} y={H + 12} textAnchor="end">{edge(rows[0])}</text>
        <text className="gos-axis" x="0" y={H + 12} textAnchor="start">{edge(rows[n - 1])}</text>
      </svg>
    </div>
  );
}

// The last daily Clarity pull (last 24 h), or why there is none yet.
function ClarityCard() {
  const [c, setC] = useState(undefined);
  useEffect(() => { fetchClarity().then(setC).catch(() => setC(null)); }, []);
  const latest = c?.latest;
  return (
    <div className="gos-card" data-clarity-card="1">
      <div className="gos-card-head"><h3>Clarity</h3>{!latest && <span className="gos-badge-soon">{c === undefined ? '…' : 'بانتظار أول سحب'}</span>}</div>
      {latest ? (
        <>
          <p className="gos-card-sub">آخر 24 ساعة حتى {new Date(latest.pull.started_at).toLocaleString('ar-SA-u-ca-gregory-nu-latn', { timeZone: 'Asia/Riyadh', dateStyle: 'medium', timeStyle: 'short' })}.</p>
          <div className="gos-clarity-nums">
            <div><b>{fmt(latest.total.sessions)}</b><span>جلسة</span></div>
            <div><b>{fmt(latest.total.users)}</b><span>مستخدم</span></div>
          </div>
          {latest.pages.length > 0 && (
            <ul className="gos-clarity-list">{latest.pages.slice(0, 4).map((p) => <li key={p.key}><span dir="ltr">{p.key}</span><b>{fmt(p.sessions)}</b></li>)}</ul>
          )}
        </>
      ) : (
        <div className="gos-empty-state">
          <strong>{c === undefined ? '…' : 'التسجيلات تعمل، والأرقام تصل بعد أول سحب يومي'}</strong>
          {c?.pulls?.[0]?.status === 'error' && <span>فشل آخر سحب. التفاصيل في «تحليل السلوك».</span>}
        </div>
      )}
      {CLARITY_URL && <a className="gos-link" href={CLARITY_URL} target="_blank" rel="noopener noreferrer">افتح Clarity ↗</a>}
    </div>
  );
}

function Soon({ title, children, badge = 'قيد الإنشاء' }) {
  return (
    <div className="gos-card">
      <div className="gos-card-head"><h3>{title}</h3>{badge && <span className="gos-badge-soon">{badge}</span>}</div>
      <div className="gos-empty-state">{children}</div>
    </div>
  );
}

const GLOSSARY = [
  ['الجلسة', 'زيارة واحدة للموقع؛ تبدأ جلسة جديدة بعد 30 دقيقة دون نشاط.'],
  ['معدل التحويل', 'نسبة من أكمل خطوة من بين من وصل إلى الخطوة التي قبلها.'],
  ['أكبر نقطة تسرّب', 'الخطوة التي يتوقف عندها أكبر عدد من الجلسات في رحلة الشراء.'],
  ['بيانات قليلة', `أقل من ${MIN_SAMPLE} في الفترة: تظهر النسبة باهتة ولا يُبنى عليها قرار.`],
  ['CTR', 'نسبة من ضغطوا على الإعلان من بين من شاهدوه. تتطلب ربط المنصات الإعلانية.'],
  ['CPC', 'متوسط ما ندفعه مقابل كل نقرة على الإعلان. يتطلب ربط المنصات الإعلانية.'],
  ['CPA', 'تكلفة الحصول على طلب واحد: الإنفاق الإعلاني ÷ الطلبات.'],
  ['ROAS', 'العائد على الإنفاق الإعلاني: الإيرادات ÷ الإنفاق.'],
];

export default function GrowthDashboard({ user, profile, lang }) {
  const [params, setParams] = useSearchParams();
  const range = useMemo(() => readRange(params), [params]);
  const win = useMemo(() => rangeWindow(range), [range]);
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);

  // only the window decides what is fetched; the comparison switch is display-only
  useEffect(() => {
    let alive = true;
    setData(null); setErr(false);
    fetchDashboard(win).then((d) => { if (alive) setData(d); })
      .catch((e) => { console.warn('[GrowthDashboard] load failed:', e?.message || e); if (alive) setErr(true); });
    return () => { alive = false; };
  }, [win.start, win.end]); // eslint-disable-line react-hooks/exhaustive-deps

  // Ad spend for the same range (read-only pulls). null = still loading; {} = none connected.
  const [ads, setAds] = useState(null);
  useEffect(() => {
    let alive = true;
    setAds(null);
    fetchAds(range, win).then((raw) => { if (alive) setAds(summarizeAds(raw)); }).catch(() => { if (alive) setAds({}); });
    return () => { alive = false; };
  }, [win.start, win.end]); // eslint-disable-line react-hooks/exhaustive-deps
  const adPlatforms = ads ? Object.values(ads).filter((p) => p.connected) : [];
  const adSpend = adPlatforms.reduce((n, p) => n + p.spend, 0);
  const adCurrency = adPlatforms.find((p) => p.currency)?.currency;
  const setRange = (next) => setParams(writeRange(next));
  const first = (profile?.full_name || '').trim().split(/\s+/)[0];
  const k = data?.kpis; const p = data?.prev;
  const none = data && k.sessions + k.productViews + k.addToCart + k.paidOrders === 0;
  const ch = (key, sampleKey = key) => data && (
    <Change now={k[key]} before={p[key]} compare={range.compare} hasPrev={data.hasPrev} sampleNow={k[sampleKey]} samplePrev={p[sampleKey]} />
  );
  const funnelBase = data?.funnel[0].n || 0;
  const lowFunnel = funnelBase < MIN_SAMPLE;
  const span = spanLabel(range.from, range.to);

  return (
    <GrowthShell user={user} profile={profile} lang={lang} active="dashboard" title="لوحة التحكم"
                 actions={<span className="gos-top-range" data-range-label="1">{span}</span>}>
      <div className="gos-hello">
        <h2>مرحباً{first ? ` ${first}` : ''}</h2>
        <p>أداء معبر — {rangeLabel(range)}. التتبّع يعمل منذ {TRACKING_SINCE}، فلا توجد بيانات سلوك قبل هذا التاريخ.</p>
      </div>
      <RangeBar range={range} onChange={setRange} />

      {err && <div className="gos-card" style={{ marginBottom: 16 }}><div className="gos-empty-state"><strong>تعذّر تحميل البيانات</strong>أعد تحميل الصفحة. إن تكرر ذلك فالخلل في الاستعلام وليس في البيانات.</div></div>}
      {data?.capped && <div className="gos-card" style={{ marginBottom: 16 }}><div className="gos-empty-state">هذه الفترة تجاوزت حدّ الصفوف؛ الأرقام أدناه ناقصة. اختر فترة أقصر.</div></div>}

      <div className="gos-kpis" aria-busy={!data}>
        <Kpi label="الجلسات" value={data ? fmt(k.sessions) : '…'} change={ch('sessions')} empty={data && !k.sessions} />
        <Kpi label="مشاهدات المنتجات" value={data ? fmt(k.productViews) : '…'} change={ch('productViews')} empty={data && !k.productViews} />
        <Kpi label="إضافات للسلة" value={data ? fmt(k.addToCart) : '…'} change={ch('addToCart')} empty={data && !k.addToCart} />
        <Kpi label="الطلبات المدفوعة" info={ORDERS_VS_TRACKING} value={data ? fmt(k.paidOrders) : '…'} change={ch('paidOrders')} empty={data && !k.paidOrders} />
        <Kpi label="الإيرادات" rtlValue value={data ? sar(k.revenue) : '…'} change={ch('revenue', 'paidOrders')} empty={data && !k.revenue} />
        {ads && adPlatforms.length > 0 ? (
          <Kpi label="الإنفاق الإعلاني" rtlValue value={`${fmt(Math.round(adSpend))} ${!adCurrency || adCurrency === 'SAR' ? 'ر.س' : adCurrency}`}
               change={<span className="gos-faint">{adPlatforms.map((p) => p.ar).join(' · ')}</span>} />
        ) : ads ? <Kpi label="الإنفاق الإعلاني" off /> : <Kpi label="الإنفاق الإعلاني" value="…" />}
      </div>

      <div className="gos-row r2">
        <Soon title="مدير النمو الذكي">
          <strong>لم يُفعَّل بعد</strong>
          سيقرأ هذه البيانات ويقترح ما يجب تقويته أو إصلاحه أو إزالته، وكل اقتراح يمر على مركز الموافقات.
        </Soon>

        <div className="gos-card">
          <div className="gos-card-head"><h3>الأداء عبر الوقت</h3></div>
          <p className="gos-card-sub">لكل مقياس رسمه الخاص على المحور الزمني نفسه ({data?.hourly ? 'بالساعة' : 'باليوم'}، بتوقيت الرياض).</p>
          {!data ? <div className="gos-empty-state">…</div> : none ? (
            <div className="gos-empty-state"><strong>لا توجد بيانات بعد</strong>ستظهر هنا الجلسات والإضافات للسلة والطلبات المدفوعة.</div>
          ) : (
            <div className="gos-mult">
              <Bars title="الجلسات" rows={data.buckets} field="sessions" total={k.sessions} hourly={data.hourly} />
              <Bars title="إضافات للسلة" rows={data.buckets} field="adds" total={k.addToCart} hourly={data.hourly} />
              <Bars title="الطلبات المدفوعة" rows={data.buckets} field="paid" total={k.paidOrders} hourly={data.hourly} />
            </div>
          )}
        </div>

        <div className="gos-card">
          <div className="gos-card-head"><h3>رحلة العميل</h3></div>
          <p className="gos-card-sub">عدد الجلسات التي وصلت إلى كل خطوة.</p>
          {!data ? <div className="gos-empty-state">…</div> : !funnelBase ? (
            <div className="gos-empty-state"><strong>لا توجد بيانات بعد</strong>تظهر الرحلة حين تصل جلسات من الموقع في هذه الفترة.</div>
          ) : (
            <>
              <div className="gos-funnel">
                {data.funnel.map((s) => (
                  <React.Fragment key={s.key}>
                    <div className="gos-fstep" data-funnel-step={s.key}>
                      <span className="gos-fstep-label">{s.label}</span>
                      <div className="gos-fbar">{s.n > 0 && <span style={{ width: `${(s.n / funnelBase) * 100}%` }} />}</div>
                      <span className="gos-fnum">{fmt(s.n)}</span>
                    </div>
                    {s.drops?.length > 0 && <ClaritySessions sessions={s.drops} label="توقفوا هنا" />}
                  </React.Fragment>
                ))}
              </div>
              {data.drop && data.drop.pct > 0 && (
                <div className={`gos-fdrop${lowFunnel ? ' low' : ''}`} data-biggest-drop={data.drop.pct} data-low-sample={lowFunnel ? '1' : '0'}>
                  أكبر نقطة تسرّب
                  <b>{data.drop.pct}%</b>
                  بين «{data.drop.from}» و«{data.drop.to}»
                  {lowFunnel && <span className="gos-low-note">{LOW_NOTE} ({fmt(funnelBase)} جلسة؛ الحد {MIN_SAMPLE}).</span>}
                </div>
              )}
            </>
          )}
          <p className="gos-note" data-orders-note="1">{ORDERS_VS_TRACKING}</p>
        </div>
      </div>

      <div className="gos-row r3">
        <div className="gos-card">
          <div className="gos-card-head"><h3>مصادر الزيارات</h3></div>
          <p className="gos-card-sub">من أين بدأت الجلسات: UTM أو معرّف النقرة أو الموقع المُحيل.</p>
          {!data ? <div className="gos-empty-state">…</div> : !data.sources.length ? (
            <div className="gos-empty-state"><strong>لا توجد بيانات بعد</strong></div>
          ) : (
            <>
              <table className="gos-table">
                <thead><tr><th>المصدر</th><th>الجلسات</th><th>أضافوا للسلة</th><th>النسبة</th></tr></thead>
                <tbody>
                  {data.sources.map((s) => {
                    const low = s.sessions < MIN_SAMPLE;
                    return (
                      <tr key={s.source} data-source={s.source}>
                        <td dir="auto">{s.source === 'direct' ? 'مباشر' : s.source}</td>
                        <td className="num">{fmt(s.sessions)}</td>
                        <td className="num">{fmt(s.added)}</td>
                        <td className={`num${low ? ' low' : ''}`} title={low ? LOW_NOTE : undefined}>{Math.round((s.added / s.sessions) * 1000) / 10}%</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {data.sources.some((s) => s.sessions < MIN_SAMPLE) && <p className="gos-note">النسب الباهتة من أقل من {MIN_SAMPLE} جلسة: {LOW_NOTE}.</p>}
            </>
          )}
        </div>

        <div className="gos-card" data-ads-card="1">
          <div className="gos-card-head"><h3>الحملات الإعلانية</h3>
            <a className="gos-link" href="/admin2/growth/campaigns">إدارة الحملات ←</a></div>
          {!ads ? <div className="gos-empty-state">…</div> : (
            <ul className="gos-clarity-list">
              {Object.values(ads).map((p) => (
                <li key={p.key} data-ads-platform={p.key}>
                  <span>{p.ar}</span>
                  <b>{p.connected ? `${fmt(Math.round(p.spend))} ${!p.currency || p.currency === 'SAR' ? 'ر.س' : p.currency}` : p.later ? 'لاحقاً' : 'غير متصل'}</b>
                </li>
              ))}
            </ul>
          )}
          <p className="gos-note">قراءة فقط. التكلفة لكل تسجيل ولكل طلب في «إدارة الحملات».</p>
        </div>

        <ClarityCard />
      </div>

      <details className="gos-card gos-gloss">
        <summary><Icon name="doc" /> دليل المصطلحات ومؤشرات الأداء</summary>
        <div className="gos-gloss-grid">
          {GLOSSARY.map(([t, d]) => (
            <div key={t} className="gos-gloss-item"><h5>{t}</h5><p>{d}</p></div>
          ))}
        </div>
      </details>
    </GrowthShell>
  );
}
